import settings from "../config/settings";
import Logger from "../common/utils/Logger";
import { getAiService } from "@ntconnect/framework/pbx";

/**
 * Setup-wizard AI assistant — calls the Messages API with a forced tool-use
 * schema so the model returns a structured PBX starter plan (extensions, ring
 * groups, IVR menus) instead of free-form text.
 *
 * Transport lives in the framework's AiService, which POSTs to the BFF's
 * transparent `/anthropic` proxy through the shared ApiClient — so the access
 * token (bearer) is attached automatically and 401s get the standard
 * refresh-and-retry. The Anthropic key stays server-side. This file owns only
 * the maritime-specific prompt/schema and the body it sends. The wizard must
 * run inside an authenticated session (the proxy 401s without a Bearer).
 */

const PLAN_TOOL = {
  name: "propose_pbx_plan",
  description:
    "Propose a starter PBX phone-system configuration tailored to the business profile. Always call this tool — do not respond with free-form text.",
  input_schema: {
    type: "object",
    properties: {
      summary: {
        type: "string",
        description:
          "1–2 sentence overview of the proposed setup, written for a non-technical business owner.",
      },
      extensions: {
        type: "array",
        description:
          "Individual user/role extensions. Numbered starting at 1001 (1001, 1002, …). Cap at 20 for the starter plan.",
        items: {
          type: "object",
          properties: {
            extension: {
              type: "string",
              description: "4-digit extension number, e.g. \"1001\".",
            },
            name: {
              type: "string",
              description:
                "Caller-ID name. Use the person's name when supplied, otherwise a role like \"Sales 1\" or \"Front Desk\".",
            },
            description: {
              type: "string",
              description: "Short note about what this extension is for.",
            },
          },
          required: ["extension", "name"],
        },
      },
      ringGroups: {
        type: "array",
        description:
          "Ring groups that fan a call out to multiple extensions at once. Numbered starting at 2001. Empty array if the business doesn't need any.",
        items: {
          type: "object",
          properties: {
            extension: {
              type: "string",
              description: "4-digit ring-group extension, e.g. \"2001\".",
            },
            name: {
              type: "string",
              description: "Display name, e.g. \"Sales\", \"Support\".",
            },
            description: { type: "string" },
            memberExtensions: {
              type: "array",
              items: { type: "string" },
              description:
                "Extension numbers (from the extensions list) that ring when this group is called.",
            },
          },
          required: ["extension", "name", "memberExtensions"],
        },
      },
      ivrs: {
        type: "array",
        description:
          "Auto-attendant menus (\"press 1 for sales…\"). At most one for the starter plan. Empty array if the business explicitly opted out.",
        items: {
          type: "object",
          properties: {
            extension: {
              type: "string",
              description: "4-digit IVR extension, e.g. \"5001\".",
            },
            name: {
              type: "string",
              description: "Menu name, e.g. \"Main Greeting\".",
            },
            greeting: {
              type: "string",
              description:
                "Suggested greeting text (used as TTS placeholder — admin can re-record later).",
            },
            options: {
              type: "array",
              description: "Menu choices, one per digit pressed.",
              items: {
                type: "object",
                properties: {
                  digit: {
                    type: "string",
                    description: "Single digit 0–9.",
                  },
                  label: {
                    type: "string",
                    description:
                      "Short label for what this option does, e.g. \"Sales\".",
                  },
                  destinationExtension: {
                    type: "string",
                    description:
                      "Extension to transfer to — prefer a ring-group extension when one fits, otherwise a user extension.",
                  },
                },
                required: ["digit", "label", "destinationExtension"],
              },
            },
          },
          required: ["extension", "name", "options"],
        },
      },
    },
    required: ["summary", "extensions", "ringGroups", "ivrs"],
  },
};

const SYSTEM_PROMPT = [
  "You are a phone-system consultant configuring a small-business PBX.",
  "Given a short business profile, propose a clean starter setup by calling the propose_pbx_plan tool.",
  "Conventions:",
  "- User extensions: 4 digits, starting at 1001 (1001, 1002, …). One per person or role.",
  "- Ring groups: starting at 2001 (2001 Sales, 2002 Support, …). Use one per department when multiple people share an inbound queue.",
  "- IVR menus: starting at 5001. At most one menu in the starter plan.",
  "- When an IVR is included, route each option to a ring group when the department has multiple people, otherwise to an individual extension.",
  "- Cap user extensions at 20 — admins can add more later.",
  "- Use real names when the user supplied them; otherwise role-based names like \"Sales 1\", \"Front Desk\".",
  "- If the user opts out of an auto-attendant, return an empty `ivrs` array.",
  "- Keep `description` and `greeting` short and friendly. No marketing copy.",
].join("\n");

function buildProfileBlock(profile) {
  if (profile.freeFormDescription) {
    return `The admin described the business in their own words:\n"""\n${profile.freeFormDescription}\n"""`;
  }
  const lines = [
    `Business type: ${profile.businessType || "(not specified)"}`,
    `Team size: ${profile.teamSize || "(not specified)"}`,
    `Departments: ${profile.departments || "(not specified)"}`,
    `Wants an auto-attendant (IVR): ${profile.wantsIvr ? "yes" : "no"}`,
  ];
  if (profile.notes) lines.push(`Additional notes: ${profile.notes}`);
  return lines.join("\n");
}

function buildUserMessage({ profile, currentPlan, feedback }) {
  // Refinement turn — show the model the prior plan + the admin's feedback
  // and ask for an updated plan. Stateless: no message history kept.
  if (currentPlan && feedback) {
    return [
      "Here's the plan you previously proposed for this business:",
      "```json",
      JSON.stringify(currentPlan, null, 2),
      "```",
      "",
      "Business profile recap:",
      buildProfileBlock(profile),
      "",
      "The admin wants the following changes applied:",
      feedback,
      "",
      "Call propose_pbx_plan again with the revised plan. Keep anything the admin didn't ask to change.",
    ].join("\n");
  }

  return [
    "Here's the business profile — propose a starter plan.",
    "",
    buildProfileBlock(profile),
  ].join("\n");
}

// Friendly messages for the BFF proxy's error contract. `status` is null on a
// network/timeout failure (the ApiClient couldn't reach the proxy).
function mapProxyError(status, detail) {
  switch (status) {
    case 401:
      // The token refresh-and-retry already ran before we get here, and AI 401s
      // no longer sign the user out — so a surviving 401 on this endpoint is
      // almost always an upstream Anthropic auth failure (bad/missing server
      // key), not the user's session. Word it for the likely cause.
      return "The AI assistant is unavailable (server key issue). Please contact support.";
    case 429:
      return "The AI assistant is busy right now. Please try again shortly.";
    case 502:
      return "The AI service is temporarily unavailable. Please try again.";
    case 503:
      return "The AI assistant isn't configured yet. Please contact support.";
    case null:
    case undefined:
      return "Couldn't reach the AI assistant. Check your connection and try again.";
    default:
      return `AI assistant error (${status}): ${(detail || "").slice(0, 240)}`;
  }
}

/**
 * Initial propose: `proposePbxPlan(profile)`.
 * Refine: `proposePbxPlan(profile, { currentPlan, feedback })`.
 */
export async function proposePbxPlan(profile, options = {}) {
  const { currentPlan, feedback } = options;

  const body = {
    model: settings.anthropicModel || "claude-sonnet-4-6",
    max_tokens: 4096,
    system: SYSTEM_PROMPT,
    tools: [PLAN_TOOL],
    tool_choice: { type: "tool", name: PLAN_TOOL.name },
    messages: [
      {
        role: "user",
        content: buildUserMessage({ profile, currentPlan, feedback }),
      },
    ],
  };

  // AiService → BFF /anthropic proxy. Bearer + 401 refresh are handled by the
  // shared ApiClient; `response` is apisauce-shaped ({ ok, data, status }).
  const response = await getAiService().createMessage(body);
  Logger("Anthropic plan response", response);

  if (!response.ok) {
    return {
      ok: false,
      error: mapProxyError(response.status, response.data?.error),
    };
  }

  // tool_use block carries the structured plan as `input`. Response shape is
  // unchanged by the proxy — same extraction as before.
  const block = (response.data?.content || []).find(
    (b) => b.type === "tool_use",
  );
  if (!block?.input) {
    return {
      ok: false,
      error: "No structured plan in the model's response.",
    };
  }

  return { ok: true, plan: block.input };
}
